import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteScroll } from "@/hooks";
import { useLazyGetBookingHistoryLogsQuery } from "@/redux/apis/booking/endpoints/actions";
import { BookingHistoryLog } from "@/redux/slices/booking/types";
import { pageState } from "@/redux/types";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { FILTER_EVENT_MAP, FILTERS } from "./fixtures";

const getFilterParams = (filters: string[]): { event?: string[]; event_name?: string[] } => {
  if (filters.length === 0 || filters.length === Object.keys(FILTERS).length) return {};

  const events = new Set<string>();
  const eventNames = new Set<string>();

  for (const key of filters) {
    const mapping = FILTER_EVENT_MAP[key as keyof typeof FILTERS];
    if (!mapping) continue;
    mapping.event.forEach((e) => events.add(e));
    mapping.event_name?.forEach((n) => eventNames.add(n));
  }

  return {
    event: events.size > 0 ? Array.from(events) : undefined,
    event_name: eventNames.size > 0 ? Array.from(eventNames) : undefined,
  };
};

interface UseInfiniteJobHistoryLogsParams {
  filters: string[];
  search: string;
}

export const useInfiniteJobHistoryLogs = ({ filters, search }: UseInfiniteJobHistoryLogsParams) => {
  const { id } = useSelectedBooking();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [getBookingHistoryLogs, { isFetching, data: logsResponse }] = useLazyGetBookingHistoryLogsQuery();
  const [logs, setLogs] = useState<BookingHistoryLog[]>([]);

  const page = logsResponse ? logsResponse.data.page : pageState;
  const { current, count } = page;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterParams = useMemo(() => getFilterParams(filters), [filters.join(",")]);
  const searchParam = search || undefined;

  useEffect(() => {
    if (logsResponse) {
      const { items, page } = logsResponse.data;
      setLogs((prev) => (page.current > 1 ? [...prev, ...items] : items));
    }
  }, [logsResponse]);

  useInfiniteScroll({
    scroller: scrollerRef.current,
    hasMore: current < count,
    pageStart: current,
    loadMore: (page: string) => getBookingHistoryLogs({ id, page, ...filterParams, search: searchParam }),
    isFetching,
  });

  return {
    getBookingHistoryLogs,
    isFetching,
    ref: scrollerRef,
    filterParams,
    searchParam,
    logs,
  };
};
